/**
 * The four colours a restaurant may change, and the rules that keep a change
 * from breaking the theme it is applied to.
 *
 * A restaurant has no block editor (D15) — the six designs are authored
 * compositions whose value is that they are internally coherent. Colour is the
 * one axis where that stays true: every skin already draws from `--store-*` and
 * derives its own greys with `color-mix`, so moving the primary moves the
 * buttons, the prices, the selected options and the check bar together. Moving
 * a section would not be so kind.
 *
 * These four are the whole surface, because they are what the token contract
 * actually has: `settings.colors.{primary, accent, surface, ink}`.
 */

export const THEME_COLOR_KEYS = ["primary", "accent", "surface", "ink"] as const;

export type ThemeColorKey = (typeof THEME_COLOR_KEYS)[number];

export type ThemeColors = Record<ThemeColorKey, string>;

/** Six-digit hex only — the shape `StorefrontThemeStyle.sanitizeColor` accepts. */
const HEX = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(value: string): boolean {
  return HEX.test(value.trim());
}

/**
 * Normalises what a colour input hands back.
 *
 * `<input type="color">` always yields lowercase `#rrggbb`, but the text field
 * beside it is a free string a seller types, and they type `1B1512` and
 * `#1b1512` in about equal measure.
 */
export function normalizeHex(value: string): string {
  const raw = value.trim();
  const withHash = raw.startsWith("#") ? raw : `#${raw}`;
  return withHash.toLowerCase();
}

/**
 * Relative luminance, per WCAG 2.1 — the sRGB channels linearised and weighted.
 *
 * Needed because the contrast question here is not decorative. The shared item
 * modal, cart drawer and check bar put a label ON the primary, and which ink
 * they need depends entirely on how light that primary is: STACK's red carries
 * white, HOMEGRILL's yellow carries near-black. A seller who moves the primary
 * moves that answer, and nothing in CSS can compute it.
 */
export function luminance(hex: string): number {
  const value = normalizeHex(hex).slice(1);
  const channel = (pair: string) => {
    const srgb = parseInt(pair, 16) / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  const r = channel(value.slice(0, 2));
  const g = channel(value.slice(2, 4));
  const b = channel(value.slice(4, 6));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hexes, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const [light, dark] = la > lb ? [la, lb] : [lb, la];
  return (light + 0.05) / (dark + 0.05);
}

/**
 * Whether a seller's pair is legible.
 *
 * 4.5:1 is WCAG AA for body text. Warned about, not blocked: this is the
 * restaurant's own brand, and a hard stop on a colour a real shopfront already
 * uses would be the tool overruling the owner. The warning says what will
 * happen; the seller decides.
 */
export function isLegible(foreground: string, background: string): boolean {
  return contrastRatio(foreground, background) >= 4.5;
}

/**
 * Reads the four colours out of a stored theme's settings, falling back to a
 * neutral that is at least VALID — a settings blob missing a colour must not
 * render an empty swatch that saves an empty string back over the theme.
 */
export function readColors(settings: unknown): ThemeColors {
  const colors =
    settings && typeof settings === "object" && "colors" in settings
      ? ((settings as { colors?: unknown }).colors ?? {})
      : {};
  const read = (key: ThemeColorKey, fallback: string) => {
    const value = (colors as Record<string, unknown>)[key];
    return typeof value === "string" && isHexColor(value)
      ? normalizeHex(value)
      : fallback;
  };
  return {
    primary: read("primary", "#1b1512"),
    accent: read("accent", "#ffd23f"),
    surface: read("surface", "#ffffff"),
    ink: read("ink", "#1b1512"),
  };
}

/** True when nothing has been changed — the Save button has nothing to write. */
export function sameColors(a: ThemeColors, b: ThemeColors): boolean {
  return THEME_COLOR_KEYS.every((key) => a[key] === b[key]);
}

/**
 * NAMED COLOURS, because an owner does not know their hex (UX-round D60, slice 10.10).
 * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
 *
 * The owner, on the editor's four fields reading `#1c1c1c`, `#000000`,
 * `#ffffff`, `#1c1c1c`: *"I do not know my hex codes. I know my sign is dark
 * green."* That is the whole finding. A colour well was already there and is
 * the right control for someone matching an exact brand value; it is the wrong
 * FIRST control for someone who can name their colour and not spell it.
 *
 * So the editor leads with these and keeps the well and the hex field for the
 * seller who does have a brand book. Twelve is a deliberate number: enough to
 * contain the colour of almost any Egyptian restaurant sign, few enough that
 * choosing is a glance. The greys and the cream are here because `surface` and
 * `ink` are two of the four keys and "white" and "near-black" are the honest
 * answers for them.
 *
 * Values are the same six-digit lowercase hex `sanitizeColor` accepts, so a
 * swatch and a typed code are indistinguishable by the time they are stored.
 */
export const THEME_SWATCHES = [
  { hex: "#1c1c1c", name: "swatchNearBlack" },
  { hex: "#495057", name: "swatchGrey" },
  { hex: "#ffffff", name: "swatchWhite" },
  { hex: "#f5f2ec", name: "swatchCream" },
  { hex: "#1f7a4d", name: "swatchDarkGreen" },
  { hex: "#2f9e44", name: "swatchGreen" },
  { hex: "#c92a2a", name: "swatchRed" },
  { hex: "#e8590c", name: "swatchOrange" },
  { hex: "#f0b429", name: "swatchYellow" },
  { hex: "#1971c2", name: "swatchBlue" },
  { hex: "#5f3dc4", name: "swatchPurple" },
  { hex: "#a6642a", name: "swatchBrown" },
] as const;

export type ThemeSwatch = (typeof THEME_SWATCHES)[number];
